<script setup>
  import { ref, computed, defineProps, defineEmits } from 'vue'
  import Clipboard from 'clipboard'
  import { ElMessage } from 'element-plus'
  import { createGuideSections, guideMarkdown } from './guide/content'

  const props = defineProps({
    meetingCode: { type: String, required: true },
    subscriptions: { type: Array, default: () => [] },
    subscriptionId: { type: [Number, String], default: '' },
  })
  const emit = defineEmits(['close', 'create'])
  const selectedId = ref(props.subscriptionId === '' ? '' : String(props.subscriptionId))
  const active = ref('start')
  const language = ref('python')
  const selected = computed(() => props.subscriptions.find((row) => String(row.id) === selectedId.value) || null)
  const sections = computed(() => createGuideSections(props.meetingCode, selected.value))
  function visibleBlocks(section) {
    return section.blocks.filter((block) => !block.languageTab || block.languageTab === language.value)
  }
  function copy(value, event) {
    const clipboard = new Clipboard(event.currentTarget, { text: () => value })
    clipboard.on('success', () => {
      ElMessage.success('已复制')
      clipboard.destroy()
    })
    clipboard.on('error', () => {
      ElMessage.error('复制失败，请手动复制')
      clipboard.destroy()
    })
    clipboard.onClick(event)
  }
  function download() {
    const url = URL.createObjectURL(new Blob([guideMarkdown(sections.value)], { type: 'text/markdown;charset=utf-8' }))
    const link = document.createElement('a')
    link.href = url
    link.download = '报名结果订阅对接指引.md'
    link.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
</script>

<template>
  <el-drawer
    custom-class="subscription-guide-drawer"
    :model-value="true"
    size="85%"
    title="报名结果订阅 · 对接指引"
    @close="emit('close')"
  >
    <div class="guide">
      <div class="guide-toolbar">
        <el-select v-model="selectedId" clearable placeholder="选择订阅，查看实际接入参数">
          <el-option
            v-for="row in subscriptions"
            :key="row.id"
            :label="`${row.receiverName}（ID ${row.id}）`"
            :value="String(row.id)"
          />
        </el-select>
        <el-button v-if="!subscriptions.length" type="primary" @click="emit('create')">新增订阅</el-button>
        <el-button @click="download">导出完整指引（含Java、Python）</el-button>
        <el-button
          :disabled="!selected"
          @click="
            copy(
              `会议/应用编码：${meetingCode}\n订阅ID：${selected.id}\n接收方：${selected.receiverName}\n回调地址：${selected.callbackUrl}\n共享密钥：请单独安全交付`,
              $event
            )
          "
        >
          复制接入参数
        </el-button>
      </div>
      <el-alert
        v-if="!selected"
        :closable="false"
        title="先选择或新增订阅获取实际订阅ID；示例ID与演示密钥不能用于正式配置"
        type="info"
      />
      <el-tabs v-model="active">
        <el-tab-pane v-for="section in sections" :key="section.id" :label="section.title" :name="section.id">
          <el-radio-group v-if="section.id === 'examples'" v-model="language" size="small">
            <el-radio-button label="python">Python</el-radio-button>
            <el-radio-button label="java">Java</el-radio-button>
          </el-radio-group>
          <template v-for="(block, index) in visibleBlocks(section)" :key="index">
            <p v-if="block.type === 'text'">{{ block.text }}</p>
            <ul v-else-if="block.type === 'list'">
              <li v-for="item in block.items" :key="item">{{ item }}</li>
            </ul>
            <table v-else-if="block.type === 'table'">
              <thead>
                <tr>
                  <th v-for="head in block.headers" :key="head">{{ head }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, rowIndex) in block.rows" :key="rowIndex">
                  <td v-for="(cell, cellIndex) in row" :key="cellIndex">{{ cell }}</td>
                </tr>
              </tbody>
            </table>
            <div
              v-else-if="block.type === 'code' && (!block.languageTab || block.languageTab === language)"
              class="code-block"
            >
              <div class="code-toolbar">
                <span>{{ block.language }}</span>
                <el-button size="small" @click="copy(block.text, $event)">复制</el-button>
              </div>
              <pre><code>{{ block.text }}</code></pre>
            </div>
          </template>
        </el-tab-pane>
      </el-tabs>
    </div>
  </el-drawer>
</template>

<style scoped>
  :global(.subscription-guide-drawer .el-drawer__body) {
    display: flex;
    min-height: 0;
    overflow: hidden;
  }
  .guide {
    flex: 1;
    min-width: 0;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    padding: 0 24px 32px;
    line-height: 1.8;
  }
  .guide-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
  }
  .guide-toolbar :deep(.el-select) {
    min-width: 300px;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0;
  }
  th,
  td {
    padding: 10px 12px;
    border: 1px solid #dcdfe6;
    text-align: left;
    overflow-wrap: anywhere;
  }
  th {
    background: #f5f7fa;
  }
  li {
    margin: 8px 0;
  }
  .code-block {
    margin: 16px 0;
    border: 1px solid #dcdfe6;
    border-radius: 6px;
    overflow: hidden;
  }
  .code-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: #f5f7fa;
  }
  pre {
    margin: 0;
    padding: 16px;
    max-height: 480px;
    overflow: auto;
    background: #18212f;
    color: #e6edf3;
    line-height: 1.65;
  }
</style>
